import periodicalClient from "./periodicalClient";
import { getPeriodicalSession, invalidatePeriodicalSession } from "./periodicalAuth";
import { withLookupCache, clearLookups } from "utils/lookupCache";

// Thin wrapper around the Periodical backend, mirroring the shape of
// src/utils/apiCall.js (getApi/postApi -> { error, result }).

const periodicalHeaders = async () => {
  const { tokenId, clientId } = await getPeriodicalSession();
  return {
    "token-id": tokenId,
    "client-id": clientId,
    "device-type": process.env.REACT_APP_PERIODICAL_DEVICE_TYPE,
  };
};

// Same rotation behavior observed in the other two modules — retry once with
// a freshly re-minted session before giving up on a 401.
const withRetryOn401 = async (makeRequest) => {
  try {
    const headers = await periodicalHeaders();
    return await makeRequest(headers);
  } catch (error) {
    if (error?.response?.status !== 401) throw error;
    invalidatePeriodicalSession();
    const headers = await periodicalHeaders();
    return await makeRequest(headers);
  }
};

const fetchGet = async (url) => {
  try {
    const response = await withRetryOn401((headers) => periodicalClient.get(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// Dropdown/master lists, cached briefly (see utils/lookupCache).
const LOOKUPS = /\/(managed_groups|managed_facilities_group|managed_contracts|managed_facilities_contract|managed_occurrences|managed_occurrence_type|managed_allocation_preference|managed_reporting_type|managed_charge_type|managed_status)\b/;
export const getPeriodicalApi = (url) => withLookupCache("work-scheduler", LOOKUPS, url, () => fetchGet(url));

export const postPeriodicalApi = async (url, data) => {
  clearLookups("work-scheduler");
  try {
    const response = await withRetryOn401((headers) => periodicalClient.post(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

export const putPeriodicalApi = async (url, data) => {
  clearLookups("work-scheduler");
  try {
    const response = await withRetryOn401((headers) => periodicalClient.put(url, data, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

export const deletePeriodicalApi = async (url) => {
  clearLookups("work-scheduler");
  try {
    const response = await withRetryOn401((headers) => periodicalClient.delete(url, { headers }));
    return { error: false, result: response.data };
  } catch (error) {
    return { error: true, result: null };
  }
};

// ---- Shared ----
export const getAllStatus = () => getPeriodicalApi("/managed_status");

// ---- Occurrence / Frequency master (screen 1) ----
export const getOccurrenceTypes = () => getPeriodicalApi("/managed_occurrence_type");
export const getOccurrences = () => getPeriodicalApi("/managed_occurrences");
export const addOccurrence = (body) => postPeriodicalApi("/managed_occurrences", body);
export const updateOccurrence = (body) => putPeriodicalApi("/managed_occurrences", body);
export const deleteOccurrence = (id) => deletePeriodicalApi(`/managed_occurrences/${id}`);

// ---- Periodicals master (screen 2) ----
// A periodical's "default frequency" references one of the named Occurrence
// records above (joined via mp003_occurrence_master in the real backend's own
// SQL), not the raw Daily/Weekly/Monthly enum — confirmed by reading
// MajesticERP-PeridicalAPI/code/models/PeriodicalsModel.js's getAllPeriodicals
// query directly. The list endpoint returns a flatter row than the single-record
// endpoint (no description/reporting_fields there) — fetch by id for editing.
export const getReportingTypes = () => getPeriodicalApi("/managed_reporting_type");
export const getAllocationPreferences = () => getPeriodicalApi("/managed_allocation_preference");
export const getPeriodicals = () => getPeriodicalApi("/managed_periodicals");
export const getPeriodicalById = (id) => getPeriodicalApi(`/managed_periodicals/${id}`);
export const addPeriodical = (body) => postPeriodicalApi("/managed_periodicals", body);
export const updatePeriodical = (body) => putPeriodicalApi("/managed_periodicals", body);
export const deletePeriodical = (id) => deletePeriodicalApi(`/managed_periodicals/${id}`);

// ---- Groups / Contracts / Facilities cascade ----
// Shared prerequisite for most of the remaining screens (statusView,
// PeriodicalStatus, Reports, Scheduling, Config) — a facility is only
// addressable in this API as a "contract facility" (a Contract x Facility
// join row, its own id — that id is what "contract_facility_id" means
// everywhere else in this module, not a raw facility id).
// getGroups/getFacilitiesByGroup are scoped to the logged-in user's own
// accessible orgs (resolved server-side from the session token, not a param);
// getContractsByGroup/getFacilitiesByContract proxy straight through to MyM's
// own /manage_contract/:id and /manage_facility_contract/:id.
export const getGroups = () => getPeriodicalApi("/managed_groups");
export const getContractsByGroup = (groupId) => getPeriodicalApi(`/managed_contracts/${groupId}`);
export const getFacilitiesByContract = (contractId) => getPeriodicalApi(`/managed_facilities_contract/${contractId}`);
export const getFacilitiesByGroup = (groupId) => getPeriodicalApi(`/managed_facilities_group/${groupId}`);

// ---- statusView: matrix allocation status ----
// Returns a rolling window (default 6 months back from Jan 1 of `year`, plus
// all 12 months of `year`) already bucketed as [{ month: "July 2026", data: [...] }],
// each data row shaped { id, planned_start_date_time, planned_end_date_time,
// contract_facility_name, periodical_name, frequency_name, status, statusString,
// assigned_users: [{user_id, user_name}] } — confirmed by reading
// MajesticERP-PeridicalAPI/code/models/MatrixAllocationModel.js directly.
export const getFacilityPeriodicalOccurrence = ({ year, contractFacilityId, userId, interval = 6 }) => {
  const params = new URLSearchParams({ year, contract_facility_id: contractFacilityId, user_id: userId, interval });
  return getPeriodicalApi(`/managed_matrix_allocation?${params.toString()}`);
};

// ---- Reports: periodical year report ----
// Same calls as the old app's Reports screen: one or more facilities in a
// single PDF. The response is { report_path: <S3 PDF URL> }, which has to be
// signed (get_presigned_url) before the browser can open it. The report's
// statuses (All, Assigned, ..., Overdue) come from mp016, not /managed_status.
export const getReportStatuses = () => getPeriodicalApi("/get_all_status");
export const createPeriodicalYearReport = ({ year, orgId, facilityIds, status }) =>
  postPeriodicalApi("/periodical_year_report", { org_id: +orgId, contract_facility_id: facilityIds, status: +status, year: +year });
export const getPresignedUrl = (url) => postPeriodicalApi("/get_presigned_url", { url });

// ---- Consolidate Report ----
// Response shape confirmed by reading ReportController.js's createPeriodicalStatusReport
// directly: { [state_name]: { [facility_name]: [{ periodical_name, frequency,
// monthly_status: ["✔"|"-", ...12] }] } } — org_id is the selected Group's id
// (not a separate "organization" concept), facility_id is "0" for all
// facilities in that group or a comma-joined list of specific facility ids
// (plain MyM facility ids here, not contract_facility_id — this screen's own
// cascade really is only Group -> Facility, no Contract step, confirmed by
// reading the old client's ConsolidateReport.js directly).
export const getPeriodicalStatusReport = ({ year, orgId, facilityId }) => {
  const params = new URLSearchParams({ org_id: orgId, fac_id: facilityId });
  return postPeriodicalApi(`/periodical_status_report?${params.toString()}`, { year });
};

// ---- Dashboard ----
// All 5 widgets share the same { year, org_id, facility_ids } query params —
// org_id is the Group id ("0"/omitted = All Groups, resolved server-side via
// MyM's own /get_all_fac_list(_by_org_id) calls), facility_ids is an optional
// comma-joined list of specific facility ids to further narrow within that
// group. Response shapes confirmed by reading DashboardController.js directly:
//   today_next7days: { due_today: { summary: {total_due_today, scheduled_today,
//     assigned_today, overdue_today}, total_tasks, tasks: [{id,
//     planned_start_date_time, contract_facility_name, periodical_name,
//     status_name}] }, next_7_days: { total_upcoming, tasks: [same shape] } }
//   completion_trend: 12 rows [{month, month_name, total_scheduled, completed, overdue}]
//   status_mix: {scheduled, assigned, in_progress, completed, cancelled, overdue}
//   frequency_distribution: {daily, weekly, monthly, quarterly, half_yearly,
//     yearly, details: [{frequency_name, occur_frequency, count}]}
//   top_overdue_facilities: top 5 [{facility_id, facility_name, total_overdue,
//     monthly_breakdown: [12 numbers]}]
const dashboardParams = ({ year, orgId, facilityIds }) => {
  const params = new URLSearchParams({ year });
  if (orgId !== undefined && orgId !== null) params.set("org_id", orgId);
  if (facilityIds) params.set("facility_ids", facilityIds);
  return params.toString();
};
export const getTodayNext7Days = (opts) => getPeriodicalApi(`/dashboard/today_next7days?${dashboardParams(opts)}`);
export const getCompletionTrend = (opts) => getPeriodicalApi(`/dashboard/completion_trend?${dashboardParams(opts)}`);
export const getStatusMix = (opts) => getPeriodicalApi(`/dashboard/status_mix?${dashboardParams(opts)}`);
export const getFrequencyDistribution = (opts) => getPeriodicalApi(`/dashboard/frequency_distribution?${dashboardParams(opts)}`);
export const getTopOverdueFacilities = (opts) => getPeriodicalApi(`/dashboard/top_overdue_facilities?${dashboardParams(opts)}`);

// ---- Periodical Config (assign periodicals to a contract-facility) ----
// Confirmed by reading ContractPeriodicalController.js directly. No single
// "available vs assigned" diff endpoint exists — the UI fetches the
// contract's full catalogue (getContractPeriodicals) and the facility's
// already-configured rows (getContractFacilityPeriodicals) separately and
// subtracts by periodical_id. The PUT endpoint is a per-item upsert (id > 0
// updates that mp008 row, no id/id 0 inserts a new one) — confirmed in the
// controller — so it's used uniformly for both assigning a new periodical
// and editing an existing assignment, rather than juggling the old app's
// separate POST-for-new/PUT-for-existing, whole-facility save-mode split.
export const getChargeTypes = () => getPeriodicalApi("/managed_charge_type");
export const getContractPeriodicals = (contractId) => getPeriodicalApi(`/managed_contract_periodicals/${contractId}`);
export const getContractFacilityPeriodicals = (contractFacilityId) => getPeriodicalApi(`/managed_contract_facility_periodicals/${contractFacilityId}`);
export const upsertContractFacilityPeriodicals = (periodicalArray) =>
  putPeriodicalApi("/managed_contract_facility_periodicals", { periodical_array: periodicalArray });
// mp008 row id (not contract_facility_id) — confirmed in the controller.
export const isWorkingOnPeriodical = (mp008Id) => getPeriodicalApi(`/is_working_peridical/${mp008Id}`);
export const removeFacilityPeriodical = (mp008Id) => getPeriodicalApi(`/remove_facility_contrct/${mp008Id}`);

// ---- Periodical Scheduling (generate occurrences, assign users, reschedule) ----
// Confirmed by reading SchedulingController.js directly. The old app's
// drag-a-card-between-months interaction is replaced here with an explicit
// "Assign / Reschedule" action per card, using the fuller /update_occurrence
// endpoint (date + user assignment together) instead of also wiring the
// lighter /update_occurrence_data (date-only) variant the old drag handler
// used — one action covers both cases the old UI split across drag vs click.
// The old app's own mask server (MajesticERP-PeridicalWeb/code/server/
// controllers/CommanController.js's getStaffList) is what the client
// actually calls for the "assign users" dropdown — it registers
// /managed_group_users/:role_id/:module_id, but its handler hardcodes
// role_id=3/module_id=2 itself (the path params are read then ignored —
// `//req.params.role_id`) and forwards to the real API's
// /get_floater_staff_for_periodical, not /managed_group_users at all.
// That's the correct, current endpoint for this list — confirmed by reading
// both the mask and PeriodicalUsersController.js's GetFloaterStaffForPeriodical
// directly, both no-arg (they forward to MyM's own /get_floater_staff_for_periodical
// with no params either).
export const getFloaterStaff = () => getPeriodicalApi("/get_floater_staff_for_periodical");
export const getPendingPeriodicals = (contractFacilityId) => getPeriodicalApi(`/getfacility_pending_all_periodical/${contractFacilityId}`);
export const getSchedulingPeriodicals = (contractFacilityId, year) =>
  getPeriodicalApi(`/getfacility_periodical_occurrence_details/${year}/${contractFacilityId}`);
export const setOccurrence = (body) => postPeriodicalApi("/set_occurrence", body);
export const revertSetOccurrence = (body) => postPeriodicalApi("/revert_set_occurrence", body);
export const updateOccurrenceAssignment = (body) => putPeriodicalApi("/update_occurrence", body);
